import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AssignmentTurnedIn,
  Group,
  Insights,
  WarningAmber,
} from '@mui/icons-material';
import {
  getCvApplicationsSummary,
  getCvApplications,
  getCvFormsSummary,
  getCvJobsSummary,
} from '../../../../services/api';

const asCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const APPLICATION_STATUS_META = {
  in_progress: { label: 'Em andamento', color: 'info' },
  passed: { label: 'Aprovada', color: 'success' },
  rejected: { label: 'Reprovada', color: 'error' },
  hired: { label: 'Contratada', color: 'primary' },
  manual: { label: 'Revisão manual', color: 'warning' },
  pending_adjust: { label: 'Pend. ajuste', color: 'warning' },
  talent_pool: { label: 'Banco de talentos', color: 'secondary' },
};

const formatDateTime = (value) => {
  if (!value) return 'Data não informada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data não informada';
  return date.toLocaleString('pt-BR');
};

const getApplicationStatusMeta = (status) => {
  const key = String(status || '').toLowerCase();
  return APPLICATION_STATUS_META[key] || { label: status || 'Sem status', color: 'default' };
};

const SectionCard = ({ title, subtitle, icon, loading, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 2.25 },
      borderRadius: 1.5,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      height: '100%',
    }}
  >
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={24} />
          <Skeleton variant="rectangular" height={24} />
          <Skeleton variant="rectangular" height={24} />
        </Stack>
      ) : (
        children
      )}
    </Stack>
  </Paper>
);

const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState(null);
  const [forms, setForms] = useState(null);
  const [apps, setApps] = useState(null);
  const [latestApplications, setLatestApplications] = useState([]);

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      try {
        const [jobsData, formsData, appsData, applicationsData] = await Promise.all([
          getCvJobsSummary(),
          getCvFormsSummary(),
          getCvApplicationsSummary(),
          getCvApplications(),
        ]);
        if (!active) return;
        setJobs(jobsData || null);
        setForms(formsData || null);
        setApps(appsData || null);
        setLatestApplications(Array.isArray(applicationsData) ? applicationsData.slice(0, 3) : []);
      } catch (error) {
        console.error('Erro ao carregar dashboard de recrutamento:', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadDashboard();

    return () => {
      active = false;
    };
  }, []);

  const totals = useMemo(() => {
    const source = apps?.totals || {};
    const inProgress = asCount(source.in_progress ?? source.inProgress);
    const passed = asCount(source.passed ?? source.approved);
    const rejected = asCount(source.rejected ?? source.declined);
    const hired = asCount(source.hired);
    const manual = asCount(source.manual);
    const pendingAdjust = asCount(source.pending_adjust ?? source.pendingAdjust);
    const total =
      asCount(source.total ?? source.all ?? source.applications)
      || (inProgress + passed + rejected + hired + manual + pendingAdjust);

    return {
      total,
      inProgress,
      passed,
      rejected,
      hired,
      manual,
      pendingAdjust,
    };
  }, [apps]);

  const slaWarnings = Array.isArray(apps?.slaWarnings) ? apps.slaWarnings : [];
  const slaCount = slaWarnings.reduce((sum, item) => sum + asCount(item?.count), 0);
  const activeJobs = asCount(jobs?.active ?? jobs?.open);
  const publishedForms = asCount(forms?.published);

  const funnelRows = [
    { key: 'in_progress', label: 'Em andamento', value: totals.inProgress, color: 'info.main' },
    { key: 'passed', label: 'Aprovadas', value: totals.passed, color: 'success.main' },
    { key: 'rejected', label: 'Reprovadas', value: totals.rejected, color: 'error.main' },
    { key: 'hired', label: 'Contratadas', value: totals.hired, color: 'primary.main' },
  ];
  const maxFunnelValue = Math.max(...funnelRows.map((row) => row.value), 1);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            background: 'linear-gradient(135deg, #f8fbff 0%, #f2f7ff 100%)',
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Painel de recrutamento
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Acompanhe o funil e execute as principais ações da operação com um clique.
              </Typography>
            </Box>

            {loading ? (
              <Stack spacing={1}>
                <Skeleton variant="rectangular" height={28} />
                <Skeleton variant="rectangular" height={52} />
              </Stack>
            ) : (
              <>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip size="small" color="primary" variant="outlined" label={`${activeJobs} vagas ativas`} />
                  <Chip size="small" color="info" variant="outlined" label={`${totals.inProgress} em andamento`} />
                  <Chip size="small" color="success" variant="outlined" label={`${publishedForms} etapas ativas`} />
                </Stack>

                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/cv/applications')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        boxShadow: (theme) => `0 8px 20px ${theme.palette.primary.main}40`,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 230 },
                        px: 3,
                      }}
                    >
                      Gerenciar candidaturas
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/cv/jobs')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 170 },
                      }}
                    >
                      Gerenciar vagas
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/cv/etapas')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 170 },
                      }}
                    >
                      Gerenciar etapas
                    </Button>
                  </Stack>
                </Paper>
              </>
            )}
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <SectionCard
              title="Saúde do funil"
              subtitle="Distribuição atual das candidaturas por status."
              icon={<Insights color="primary" />}
              loading={loading}
            >
              <Stack spacing={1.25}>
                {funnelRows.map((row) => (
                  <Box key={row.key}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{row.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.value}
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 1, mt: 0.5 }}>
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 1,
                          bgcolor: row.color,
                          width: `${(row.value / maxFunnelValue) * 100}%`,
                          transition: 'width 0.25s ease',
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Total no funil: {totals.total}
                </Typography>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionCard
              title="Últimas candidaturas"
              icon={<Group color="primary" />}
              loading={loading}
            >
              {latestApplications.length > 0 ? (
                <Stack spacing={1}>
                  {latestApplications.map((application, index) => {
                    const statusInfo = getApplicationStatusMeta(application?.status);
                    const targetPath = application?.job_id
                      ? `/cv/applications?jobId=${application.job_id}`
                      : '/cv/applications';
                    return (
                      <Paper
                        key={application?.id || `application-${index}`}
                        variant="outlined"
                        onClick={() => navigate(targetPath)}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.25,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, transform 0.2s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {application?.candidate_name || 'Candidato não informado'}
                            </Typography>
                            <Chip size="small" color={statusInfo.color} label={statusInfo.label} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(application?.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Vaga: {application?.job_title || 'Vaga não informada'}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Ainda não há candidaturas recentes para exibir.
                </Typography>
              )}
            </SectionCard>
          </Grid>
        </Grid>

        {slaCount > 0 && !loading && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: (theme) => `1px solid ${theme.palette.warning.main}`,
              bgcolor: 'warning.50',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <WarningAmber color="warning" />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Existem {slaCount} alertas de SLA para candidaturas aguardando revisão há mais de 48h.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<AssignmentTurnedIn />}
                onClick={() => navigate('/cv/applications')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Priorizar agora
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default Home;
